import { useState } from 'react';

import { getCookie } from 'cookies-next';

import { useChatGPTContext } from '@contexts/chatGPT';
import { useAuthState } from '@contexts/auth/AuthContext';
type Message = {
  text: string;
  sender: 'user' | 'assistant';
};
const useChatApi = () => {
  const base_url = process.env.NEXT_PUBLIC_CHAT_GPT_URL;
  const { loginInfo } = useAuthState();

  const [isTyping, setIsTyping] = useState(false);
  const { itemId, itemType } = useChatGPTContext();

  const [messages, setMessages] = useState<Message[]>(() => {
    const storedMessages = sessionStorage.getItem('chatGPTMessages');
    return storedMessages ? JSON.parse(storedMessages) : [];
  });

  const sessionId = getCookie('sessionId');

  const handleOnFinished = async (LLMMessage) => {
    await fetch(base_url + '/on_finished', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ session_id: sessionId, message: LLMMessage }),
    });
  };

  const prepareMessage = (prevMessages, accumulatedText) => {
    const outputMessage = prevMessages[prevMessages.length - 1];
    outputMessage.text = accumulatedText;
    outputMessage.sender = 'assistant';
    return outputMessage;
  };

  const prepareRequestBody = (updatedMessages) => {
    const basePayload = {
      session_id: sessionId,
      messages: updatedMessages,
      page_url: window.location.href,
      source: 'setur-web',
    };

    if (loginInfo?.userId) {
      const userPayload = {
        ...basePayload,
        user_id: loginInfo.userId.toString(),
        loyalty_id: loginInfo.loyaltyUser?.customer?.id,
      };

      if (itemId && itemType) {
        return JSON.stringify({
          ...userPayload,
          item_id: itemId.toString(),
          item_type: itemType,
        });
      }
      return JSON.stringify(userPayload);
    }

    if (itemId && itemType) {
      return JSON.stringify({
        ...basePayload,
        item_id: itemId.toString(),
        item_type: itemType,
      });
    }

    return JSON.stringify(basePayload);
  };

  const handleChat = async (updatedMessages) => {
    let accumulatedText = '';

    setIsTyping(true);
    const timeoutId = setTimeout(() => {
      setIsTyping(false);
      accumulatedText =
        'Şu anda sunucularındaki yoğunluktan dolayı cevap veremiyorum. Lütfen daha sonra tekrar deneyiniz.';
      setMessages((prevMessages) => {
        const outputMessage = prepareMessage(prevMessages, accumulatedText);
        handleOnFinished(accumulatedText);
        return [...prevMessages.slice(0, -1), outputMessage];
      });
    }, 120000);

    fetch(base_url + '/chat_stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: prepareRequestBody(updatedMessages),
    })
      .then((response) => {
        const reader = response?.body?.getReader();
        if (reader) {
          return new ReadableStream({
            async start(controller) {
              let done = false;
              while (!done) {
                const { done: isDone, value } = await reader.read();
                done = isDone;
                if (done) {
                  break;
                }
                const newToken = new TextDecoder().decode(value);
                accumulatedText += newToken;
                controller.enqueue(newToken);
              }
              controller.close();
              reader.releaseLock();
            },
          });
        }
      })
      .then((stream) => {
        updatedMessages = [
          ...updatedMessages,
          { text: '', sender: 'assistant' },
        ];
        setMessages(updatedMessages);
        const reader = stream?.getReader();
        if (reader) {
          reader.read().then(function processText({ done }) {
            if (done) {
              clearTimeout(timeoutId);
              setIsTyping(false);
              if (!accumulatedText) {
                accumulatedText =
                  'Şu anda sunucularındaki yoğunluktan dolayı cevap veremiyorum. Lütfen daha sonra tekrar deneyiniz.';
                return setMessages((prevMessages) => {
                  const outputMessage = prepareMessage(
                    prevMessages,
                    accumulatedText
                  );
                  return [...prevMessages.slice(0, -1), outputMessage];
                });
              }
              handleOnFinished(accumulatedText);
              return;
            }
            setMessages((prevMessages) => {
              const outputMessage = prepareMessage(
                prevMessages,
                accumulatedText
              );
              return [...prevMessages.slice(0, -1), outputMessage];
            });
            return reader.read().then(processText);
          });
        }
      });
  };
  return { handleChat, isTyping, messages, setMessages };
};

export default useChatApi;
